/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useMemo, useState } from "react"
import { Platform, View } from "react-native"
import { router } from "expo-router"
import { useUser } from "@/queries/user"
import TextTabs, { TextTab } from "@/components/tabs/TextTabs"
import { TouchableOpacity } from "react-native-gesture-handler"
import SortArrows from "@/assets/icons/sort-arrows.svg"
import BuyBoxIcon from "@/assets/icons/buy-box.svg"
import BuyBoxInactiveIcon from "@/assets/icons/buy-box-inactive.svg"
import BuyBoxTooltip from "@/components/buybox/BuyBoxTooltip"
import { useStore } from "@nanostores/react"
import { $isMyLeadsRunning, setIsMyLeadsRunning } from "@/stores/myLeads"
import {
  myLeadsViewed,
  propertiesViewed,
  sortClicked,
} from "@/analytics/events/properties"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { H2 } from "@/components/standard/BFText"
import {
  ANALYSIS_TAB_LABELS,
  type AnalysisListType,
} from "@/screens/analyze/utils/Properties"
import { AnalysisList } from "@/screens/analyze/components/AnalysisList"
import { NotificationToast } from "@/screens/analyze/components/NotificationToast"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import ReusableSortPopup, {
  type SortSectionConfig,
} from "@/components/sorting/ReusableSortPopup"
import type { AnalysesSortOption, SortDirectionOption } from "@/types"
import { getOrderingLabels } from "@/utils/AnalysisUtils"
import { useMyLeadsBoostFeature } from "@/hooks/useMyLeadsBoostFeature"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"

export default function PropertiesScreen() {
  const [sortOpen, setSortOpen] = useState(false)
  const [tab, setTab] = useState<AnalysisListType>("all")
  const [sort, setSort] = useState<AnalysesSortOption>("updated_at")
  const [direction, setDirection] = useState<SortDirectionOption>("default")
  const { isWeb } = useDeviceAttributes()

  const { data: user } = useUser()

  const {
    featureIsEnabled: myLeadsBoostEnabled,
    myLeadsEnabled,
    hasAnalyses,
    hasBuyBox,
  } = useMyLeadsBoostFeature()
  const isMyLeadsRunning = useStore($isMyLeadsRunning)

  const sortPopupTop: number = Platform.select({
    android: 64,
    ios: 30,
    web: 60,
    default: 120,
  })

  const analysisSortingSections: SortSectionConfig[] = useMemo(() => {
    const orderingLabels = getOrderingLabels(sort)
    const sections: Array<SortSectionConfig | null> = [
      {
        header: "ACTIVITY",
        options: [
          { label: "Date", value: "updated_at", defaultDirection: "reverse" },
          { label: "Profit", value: "profit" },
          { label: "ARV", value: "after_repair_value" },
          { label: "Purchase Price", value: "purchase_price" },
          { label: "Beds", value: "bedrooms" },
          { label: "Year Built", value: "year_built" },
          { label: "Email Sourced", value: "source_email" },
          { label: "Analyzed Properties", value: "is_sourced" },
        ],
      },
      orderingLabels
        ? {
            header: "ORDERING",
            options: [
              { label: orderingLabels.ascending, value: "default" },
              { label: orderingLabels.descending, value: "reverse" },
            ],
          }
        : null,
    ]
    return sections.filter(Boolean) as SortSectionConfig[]
  }, [sort])

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    if (user?.me) {
      if (user.me.googleToken && !user.me.googleToken.invalidAt) {
        myLeadsViewed({
          currentTab: tab,
        })
      } else {
        propertiesViewed({
          currentTab: tab,
        })
      }
    }
  }, [user])

  const onBuyBox = () => {
    router.push("/buybox")
  }

  // Conditionally render header based on Control or Variant
  const headerContent = (
    <View className="flex flex-col justify-end py-2">
      <View className="flex-row justify-between items-end">
        <H2 className="text-lightest">{"Properties"}</H2>
        <View className="flex flex-row">
          {myLeadsEnabled && (
            <View className="mr-4">
              <TouchableOpacity testID={"buy-box-button"} onPress={onBuyBox}>
                {hasBuyBox ? <BuyBoxIcon /> : <BuyBoxInactiveIcon />}
              </TouchableOpacity>
            </View>
          )}
          <TouchableOpacity
            testID="sort-button"
            disabled={!hasAnalyses}
            onPress={() => {
              if (!sortOpen) {
                sortClicked()
                setSortOpen(true)
              } else {
                setSortOpen(false)
              }
              // Removing timeout and using the Reusable Sort Popup component fixes the issue where
              // tapping away from the sort popup often inadvertently opened a card
            }}
          >
            {hasAnalyses ? <SortArrows /> : null}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  )

  const entryObjects = Object.entries(ANALYSIS_TAB_LABELS)
  const dimmedBuyBox = !myLeadsEnabled || !hasBuyBox

  const shouldHideBoostFeatureTabs = !myLeadsBoostEnabled

  const onSelectSort = (value: string) => {
    const selectedOption = analysisSortingSections
      .flatMap((section) => section.options)
      .find((option) => option.value === value)

    setSort(value as AnalysesSortOption)
    setDirection(selectedOption?.defaultDirection ?? "default")
    setSortOpen(false)
  }

  return (
    <>
      <BFScreen
        className="flex-col"
        noBackButton={true}
        headerSticky={false}
        dark={true}
        gradientBackground={true}
        includeStandardSideMargins={true}
        header={headerContent}
      >
        <View className="flex-1">
          <TextTabs hasFullWidthLine={!isWeb}>
            {entryObjects.map(([key, label]) => {
              if (
                (shouldHideBoostFeatureTabs && key === "buy_box") ||
                (shouldHideBoostFeatureTabs && key === "my_leads")
              ) {
                return null
              }
              return (
                <TextTab
                  label={label}
                  value={key}
                  key={key}
                  selected={key === tab}
                  onSelect={() => {
                    setTab(key)
                  }}
                  size={entryObjects.length > 3 ? "sm" : "lg"}
                  dark={true}
                  isDimmed={dimmedBuyBox && key === "buy_box"}
                  disabled={dimmedBuyBox && key === "buy_box"}
                  // badge={user.badges?.unreadAnalysisCount || 0}
                />
              )
            })}
          </TextTabs>

          <AnalysisList
            sort={sort}
            direction={direction}
            onlyFavorites={tab === "favorites"}
            onlySourced={tab === "my_leads"}
            buyBoxId={tab === "buy_box" ? user?.me?.buyBox?.id! : null}
          />
          {isMyLeadsRunning && (
            <NotificationToast
              message="Currently processing your leads...almost there!"
              onDismiss={() => {
                setIsMyLeadsRunning(false)
              }}
            />
          )}
          <ReusableSortPopup
            displays={sortOpen}
            sort={sort}
            direction={direction}
            sections={analysisSortingSections}
            positionClassName={`right-0 top-[${sortPopupTop}px]`}
            onSelectSort={onSelectSort} // Use the modified function
            onSelectDirection={(direction: SortDirectionOption) => {
              setDirection(direction)
              setSortOpen(false)
            }}
            onClose={() => {
              setSortOpen(false)
            }}
          />
        </View>
      </BFScreen>
      {myLeadsEnabled && !hasBuyBox && <BuyBoxTooltip />}
    </>
  )
}
